import React, { useEffect, useRef } from 'react';

import classNames from 'classnames';

import { formatDateLong } from 'Helpers/dateFormat';
import { modalOptions } from 'Helpers/modalOptions';

import Badge from 'CommonComponents/Badge';
import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';
import TextButton from 'CommonComponents/TextButton';

import useModal from 'Hooks/useModal';

/**
 * A read-only list of the organisation's versions — name, period and role count,
 * with a click to switch the rate card to that version. Not an audit trail of
 * rate edits.
 */
export default function RateCardVersionHistoryModal({ versions, selectedVersionId, onSelect, onClose }) {
    const modal = useModal({ options: { ...modalOptions(false), onHide: onClose }, visible: true });

    const modalStateRef = useRef(modal);
    modalStateRef.current = modal;

    useEffect(
        () => () => {
            const { element } = modalStateRef.current;

            if (element && element.isVisible()) {
                element.hide();
            }
        },
        [],
    );

    const close = () => modal.hide();

    return (
        <Modal id="rate-card-version-history-modal" modal={modal}>
            <ModalHeader title={$t('Version history')} close={close} />

            <div className="modal__form-container">
                {versions.length === 0 && <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('No versions yet.')}</p>}

                {versions.map((version) => (
                    <div
                        key={version.id}
                        className={classNames('tw-flex tw-items-center tw-justify-between tw-gap-4 tw-rounded-lg tw-border-2 tw-p-3', {
                            'tw-border-blue': version.id === selectedVersionId,
                            'tw-border-grey': version.id !== selectedVersionId,
                        })}
                    >
                        <div className="tw-flex tw-flex-col tw-gap-1">
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <span className="tw-text-sm tw-font-semibold tw-text-charcoal">{version.name}</span>
                                {version.is_current && <Badge variant="success">{$t('Current')}</Badge>}
                            </div>
                            <span className="tw-text-xs tw-text-charcoal-75">
                                {formatDateLong(version.starts_on)} – {formatDateLong(version.ends_on)}
                            </span>
                        </div>

                        <div className="tw-flex tw-items-center tw-gap-4">
                            <span className="tw-text-xs tw-text-charcoal-75">{$t('{{count}} roles', { count: version.roles_count ?? 0 })}</span>
                            <TextButton
                                variant="primary"
                                onClick={() => {
                                    onSelect(version.id);
                                    close();
                                }}
                            >
                                {$t('View')}
                            </TextButton>
                        </div>
                    </div>
                ))}
            </div>
        </Modal>
    );
}
